<template>
  <BaseDialog
    :title="dialogTitle"
    width="1140px"
    :bodyLoading="loading.list"
    hideCancel
    hideConfirm
    class="pickup-reservation-dialog"
    @close="$emit('close')"
  >
    <div class="reservation-list" :class="{ 'is-empty': !reservations.length }">
      <EmptyBlock v-if="!loading.list && !reservations.length" />
      <div
        v-for="reservation in reservations"
        :key="reservation.id"
        class="booking-card"
        :class="{ done: getStatus(reservation) === 'complete' }"
      >
        <div class="booking-field">
          <span class="booking-label">{{ $t('kachogawaPickupOverview.dialog.userName.title') }}</span>
          <span class="booking-value">{{ reservation.userName || $t('common.unfilled.text') }}</span>
        </div>
        <div class="booking-field">
          <span class="booking-label">{{ $t('kachogawaPickupOverview.dialog.userPhone.title') }}</span>
          <span class="booking-value">{{ reservation.userPhone || $t('common.unfilled.text') }}</span>
        </div>
        <div class="booking-field">
          <span class="booking-label">{{ $t('kachogawaPickupOverview.dialog.category.title') }}</span>
          <span class="booking-value">{{ getCategoryName(reservation) }}</span>
        </div>
        <div class="booking-field">
          <span class="booking-label">{{ $t('kachogawaPickupOverview.dialog.items.title') }}</span>
          <span class="booking-value">{{ getItemsText(reservation) }}</span>
        </div>
        <div class="booking-field">
          <span class="booking-label">{{ $t('kachogawaPickupOverview.dialog.status.title') }}</span>
          <span class="booking-pill" :class="`pill-${getStatus(reservation)}`">
            {{ statusName(getStatus(reservation)) }}
          </span>
        </div>
        <div class="booking-field">
          <span class="booking-label">{{ $t('kachogawaPickupOverview.dialog.note.title') }}</span>
          <span class="booking-value booking-note">{{ getAdminComment(reservation) || '—' }}</span>
        </div>
        <div class="booking-actions">
          <BaseElButton
            v-if="canOperate(reservation)"
            type="primary"
            size="md"
            :loading="updatingId === reservation.id"
            @click="updateStatus(reservation, 'complete')"
          >
            {{ $t('kachogawaPickupOverview.dialog.action.complete.text') }}
          </BaseElButton>
          <BaseElButton
            v-if="canOperate(reservation)"
            plain
            size="md"
            :loading="updatingId === reservation.id"
            @click="updateStatus(reservation, 'absence')"
          >
            {{ $t('kachogawaPickupOverview.dialog.action.absence.text') }}
          </BaseElButton>
          <BaseElButton
            plain
            size="md"
            @click="openNoteEditor(reservation)"
          >
            {{ $t('kachogawaPickupOverview.dialog.action.note.text') }}
          </BaseElButton>
        </div>
      </div>
    </div>

    <EditAdminCommentDialog
      v-if="noteEditor.visible"
      :reservation="noteEditor.reservation"
      @close="closeNoteEditor"
      @updated="onNoteUpdated"
    />
  </BaseDialog>
</template>

<script>
import { computed, defineComponent, onMounted, reactive, ref } from 'vue'
import dayjs from '@/lib/dayjs'
import { get, map, join, includes, find, filter } from 'lodash'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import EmptyBlock from '@/components/EmptyBlock.vue'
import EditAdminCommentDialog from './EditAdminCommentDialog.vue'
import { useTable } from '@/use/table'
import { i18n } from '@/plugins/i18n/i18n'
import { orderStatusConfig } from '@/config/reservation'
import { GetReservation, UpdateReservationOrderStatus } from '@/api/reservation'

export default defineComponent({
  name: 'PickupReservationDialog',
  components: { BaseDialog, EmptyBlock, EditAdminCommentDialog },
  props: {
    bucket: {
      type: Object,
      required: true,
    },
    status: {
      type: String,
      default: 'wait',
    },
    appointmentCategoryId: {
      type: String,
      default: null,
    },
    categoryName: {
      type: String,
      default: '',
    },
    categoryOptions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'updated'],
  setup (props, { emit }) {
    const { shopId, loading, extendData } = useTable()
    const reservations = ref([])
    const updatingId = ref(null)
    const noteEditor = reactive({
      visible: false,
      reservation: null,
    })

    const dialogTitle = computed(() => {
      const dateText = dayjs(props.bucket.date).format('YYYY/MM/DD')
      return i18n.t('kachogawaPickupOverview.dialog.title', {
        category: props.categoryName,
        date: dateText,
        time: props.bucket.hour,
      })
    })

    // 預約狀態存於 AppointmentOrder.status（與預約列表一致），非 reservation.status
    const getStatus = (reservation) => {
      return get(reservation, 'AppointmentOrder.status', '')
    }

    const statusName = (status) => {
      const config = get(orderStatusConfig, status)
      return config ? i18n.t(config.name) : status
    }

    // 預約僅帶 AppointmentCategoryId（未含關聯物件），以總覽頁的類別清單對照名稱；
    // 無類別者顯示「未分類」，與右側分店備貨明細 pivot 一致
    const getCategoryName = (reservation) => {
      const categoryId = get(reservation, 'AppointmentCategoryId')
      if (!categoryId) return i18n.t('kachogawaPickupOverview.pivot.uncategorized.title')
      const found = find(props.categoryOptions, (c) => c.id === categoryId)
      return get(found, 'name') || i18n.t('kachogawaPickupOverview.pivot.uncategorized.title')
    }

    // 品項與數量存於 AppointmentOrder.itemizedDetails（含服務與附加項），非頂層關聯
    const getItemsText = (reservation) => {
      const items = get(reservation, 'AppointmentOrder.itemizedDetails', [])
      const parts = map(filter(items, (i) => i.group === 'item'), (i) =>
        i.quantity > 1 ? `${i.name} x${i.quantity}` : i.name,
      )
      return parts.length ? join(parts, '、') : '—'
    }

    // 顯示門市內部備註（adminComment），非客人輸入的 userComment
    const getAdminComment = (reservation) => {
      return get(reservation, 'AppointmentOrder.adminComment', '')
    }

    // 僅「已預約 / 確認中」可進行取貨 / 未到操作
    const canOperate = (reservation) => {
      return includes(['wait', 'confirm'], getStatus(reservation))
    }

    const getReservations = async () => {
      extendData('loading', { list: true })
      try {
        // 只撈點選的「整點時段」明細；後端以 start >= dateStart && start <= dateEnd 篩選，
        // 須帶完整時間（純日期會被解析成當天 00:00 導致整天白天紀錄被排除）
        const slotStart = dayjs(`${props.bucket.date} ${props.bucket.hour}`)
        const res = await GetReservation({
          shopId: shopId.value,
          dateStart: slotStart.startOf('hour').toDate(),
          dateEnd: slotStart.endOf('hour').toDate(),
          status: props.status,
          AppointmentCategoryId: props.appointmentCategoryId || undefined,
          start: 0,
          limit: 100,
        })
        reservations.value = res || []
      } catch (err) {
        reservations.value = []
        window.$message.error(err)
      } finally {
        extendData('loading', { list: false })
      }
    }

    const updateStatus = async (reservation, status) => {
      const orderId = get(reservation, 'AppointmentOrder.id')
      updatingId.value = reservation.id
      const [, err] = await UpdateReservationOrderStatus({
        shopId: shopId.value,
        id: orderId,
        status,
      })
      updatingId.value = null
      if (err) return window.$message.error(err)
      window.$message.success(i18n.t('common.message.updateSuccess'))
      await getReservations()
      emit('updated')
    }

    const openNoteEditor = (reservation) => {
      noteEditor.reservation = reservation
      noteEditor.visible = true
    }

    const closeNoteEditor = () => {
      noteEditor.visible = false
      noteEditor.reservation = null
    }

    const onNoteUpdated = async () => {
      await getReservations()
      emit('updated')
    }

    onMounted(getReservations)

    return {
      loading,
      reservations,
      updatingId,
      noteEditor,
      dialogTitle,
      getStatus,
      statusName,
      getCategoryName,
      getItemsText,
      getAdminComment,
      canOperate,
      updateStatus,
      openNoteEditor,
      closeNoteEditor,
      onNoteUpdated,
    }
  },
})
</script>

<style lang="postcss" scoped>
/* 已有右上角關閉按鈕，footer 不需要 */
:deep(.pickup-reservation-dialog .el-dialog__footer) {
  display: none;
}

.reservation-list {
  @apply flex flex-col gap-[12px] max-h-[64vh] overflow-y-auto;
}

.reservation-list.is-empty {
  @apply min-h-[160px];
}

/* 固定欄寬：避免不同狀態下按鈕數量變化導致前面欄位排版錯位 */
.booking-card {
  @apply grid items-center gap-x-[16px] gap-y-[12px];
  @apply bg-white border border-gray-30 rounded-sub py-[16px] px-[20px];
  grid-template-columns:
    72px      /* 客人姓名 */
    104px     /* 聯絡電話 */
    88px      /* 分店 */
    160px     /* 商品明細 */
    88px      /* 預約狀態 */
    104px     /* 備註 */
    320px;    /* 操作按鈕區（橫排 3 顆） */
}

.booking-card.done {
  @apply opacity-60;
}

.booking-field {
  @apply flex flex-col gap-[6px] min-w-0;
}

.booking-label {
  @apply text-[12px] text-gray-60;
}

.booking-value {
  @apply text-sub text-gray-100 break-words;
}

.booking-value.booking-note {
  @apply text-[12px] text-gray-80;
}

.booking-pill {
  @apply inline-flex items-center justify-center self-start;
  @apply px-[12px] py-[4px] rounded-full text-sub font-medium whitespace-nowrap;
}

.pill-wait { @apply bg-info-light text-info; }
.pill-confirm { @apply bg-warning-light text-warning; }
.pill-complete { @apply bg-action-light text-action; }
.pill-absence { @apply bg-gray-30 text-gray-80; }
.pill-cancel { @apply bg-danger-light text-danger; }

.booking-actions {
  @apply flex flex-row flex-wrap gap-[8px] items-center self-center;
}

.booking-actions :deep(.el-button) {
  @apply m-0;
}
</style>
